import React from 'react'
import FormControl from '~/components/FormControl'
import HelperText from '~/components/HelperText'
import LabelText from '~/components/LabelText'
import { fixedForwardRef } from '~/helpers/react'

export type BaseTextFieldProps = PropsWithClassName<{
  fullWidth?: boolean
  isError?: boolean
  label?: string
  htmlFor?: string
  helperText?: string
}>

export type TextFieldProps<T extends boolean = false> = {
  isTextarea?: T
} & React.ComponentPropsWithoutRef<T extends true ? 'textarea' : 'input'> &
  BaseTextFieldProps

const TextField = fixedForwardRef(
  <T extends boolean = false>(
    {
      fullWidth = false,
      isError = false,
      label = '',
      helperText = '',
      isTextarea = false as T,
      htmlFor,
      className,
      ...restInputProps
    }: TextFieldProps<T>,
    ref: React.ForwardedRef<
      T extends true ? HTMLTextAreaElement : HTMLInputElement
    >
  ) => {
    const ariaDescribedByFallback = `${React.useId()}-helper`
    const ariaDescribedBy = helperText
      ? restInputProps['aria-describedby'] ||
        (restInputProps.name ? `${restInputProps.name}-helper` : '') ||
        ariaDescribedByFallback
      : ''

    const ComponentInput = isTextarea ? 'textarea' : 'input'

    return (
      <FormControl
        fullWidth={fullWidth}
        className={className}
        disabled={restInputProps.disabled}
      >
        <label htmlFor={htmlFor} className="group">
          {label ? (
            <div className="flex justify-start">
              <LabelText isError={isError} isRequired={restInputProps.required}>
                {label}
              </LabelText>
            </div>
          ) : null}
          <div
            data-wrapper-input
            className="border-b border-black/40 py-3 pl-0 pr-6 group-focus-within:border-black group-hover:border-black/80"
          >
            <ComponentInput
              type="text"
              className="text-body w-full text-input text-black outline-none placeholder:text-black/30"
              aria-invalid={isError}
              id={htmlFor}
              {...restInputProps}
              aria-describedby={ariaDescribedBy}
              // TODO: fix this by using generic
              // @ts-expect-error: it doesn't know that ref is a valid ref
              ref={ref}
            />
          </div>
        </label>
        {helperText ? (
          <HelperText id={ariaDescribedBy} isError={isError}>
            {helperText}
          </HelperText>
        ) : null}
      </FormControl>
    )
  }
)

export default TextField
